import AdminShell from '@ui/AdminShell'
import { lazy, Suspense } from 'react'
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
} from 'react-router-dom'

import CurrentAccountProvider from '@/contexts/CurrentAccountProvider'
import CurrentUserProvider from '@/contexts/CurrentUserProvider'
import TeamsModeGuard from '@/pages/events/TeamsModeGuard'
import Login from '@/pages/Login'

import AuthGuard from './AuthGuard'
import PlatformGuard from './PlatformGuard'

/**
 * Every route below the shell is lazy, so a visitor downloads only the surface
 * they asked for. This matters because one bundle now serves three audiences
 * with very different connections: an admin at a desk, a projector on a kiosk,
 * and the floor on a phone on a golf course. Eagerly importing the pages put all
 * 26 of them in the entry chunk (1,373 kB), which the floor paid for just to
 * reach a poker clock.
 *
 * `Login` and `TeamsModeGuard` stay eager: Login is the cold-start destination
 * for unauthenticated visitors (lazy-loading it only adds a round trip), and
 * TeamsModeGuard is a tiny wrapper, not a page.
 */
const AccountSettings = lazy(
  () => import('@/pages/account-settings/AccountSettings')
)
const AccountSelect = lazy(() => import('@/pages/AccountSelect'))
const CourseDetail = lazy(() => import('@/pages/courses/CourseDetail'))
const CoursesList = lazy(() => import('@/pages/courses/CoursesList'))
const EventDetailShell = lazy(() => import('@/pages/events/EventDetailShell'))
const EventFlights = lazy(() => import('@/pages/events/EventFlights'))
const EventPlayers = lazy(() => import('@/pages/events/EventPlayers'))
const EventPlayerScorecard = lazy(
  () => import('@/pages/events/EventPlayerScorecard')
)
const EventSettings = lazy(() => import('@/pages/events/EventSettings'))
const EventsList = lazy(() => import('@/pages/events/EventsList'))
const EventSummary = lazy(() => import('@/pages/events/EventSummary'))
const EventTeams = lazy(() => import('@/pages/events/EventTeams'))
const EventPoker = lazy(() => import('@/pages/events/poker/EventPoker'))
const AccountDetail = lazy(
  () => import('@/pages/platform/accounts/AccountDetail')
)
const AccountsList = lazy(
  () => import('@/pages/platform/accounts/AccountsList')
)
const DevTools = lazy(() => import('@/pages/platform/dev-tools/DevTools'))
const Reports = lazy(() => import('@/pages/platform/reports/Reports'))
const Sms = lazy(() => import('@/pages/platform/sms/Sms'))
const VippsPayments = lazy(
  () => import('@/pages/platform/vipps-payments/VippsPayments')
)
const PublicRegistrationRoot = lazy(
  () => import('@/pages/registration/PublicRegistrationRoot')
)
const RegistrationAborted = lazy(
  () => import('@/pages/registration/RegistrationAborted')
)
const RegistrationEntry = lazy(
  () => import('@/pages/registration/RegistrationEntry')
)
const RegistrationSummary = lazy(
  () => import('@/pages/registration/RegistrationSummary')
)
const Users = lazy(() => import('@/pages/Users'))

// Keyed floor/TD surface — see the /o/:operatorKey route below.
const OperatorKeyLoader = lazy(
  () => import('@/pages/operator/OperatorKeyLoader')
)
const OperatorShell = lazy(() => import('@/pages/operator/OperatorShell'))

// Keyed kiosk display — see the /p/:eventKey route below.
const ProjectorKeyLoader = lazy(
  () => import('@/pages/projector/EventKeyLoader')
)
const ProjectorShell = lazy(() => import('@/pages/projector/ProjectorShell'))

const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  { path: '/account-select', element: <AccountSelect /> },
  {
    path: '/registration/:nanoId',
    element: <PublicRegistrationRoot />,
    children: [
      { index: true, element: <RegistrationEntry /> },
      { path: 'player/:playerNanoId', element: <RegistrationSummary /> },
      { path: 'aborted', element: <RegistrationAborted /> },
    ],
  },
  {
    // Keyed floor/TD surface — no login, `operator-key` header auth, and
    // deliberately outside AuthGuard/AdminShell (same shape as /registration).
    path: '/o/:operatorKey',
    element: (
      <Suspense fallback={null}>
        <OperatorKeyLoader />
      </Suspense>
    ),
    children: [{ index: true, element: <OperatorShell /> }],
  },
  {
    // Keyed kiosk display — no login, `polf-event-key` header auth, outside
    // AuthGuard/AdminShell. Reuses PublicApolloProvider with /registration:
    // same auth mode, and it's keyed on the nanoId so each event gets a cold
    // cache.
    path: '/p/:eventKey',
    element: (
      <Suspense fallback={null}>
        <ProjectorKeyLoader />
      </Suspense>
    ),
    children: [{ index: true, element: <ProjectorShell /> }],
  },
  {
    path: '/',
    element: (
      <AuthGuard>
        <CurrentUserProvider>
          <CurrentAccountProvider>
            <AdminShell />
          </CurrentAccountProvider>
        </CurrentUserProvider>
      </AuthGuard>
    ),
    children: [
      { index: true, element: <Navigate to="/events" replace /> },
      { path: 'events', element: <EventsList /> },
      {
        path: 'events/:id',
        element: <EventDetailShell />,
        children: [
          { index: true, element: <Navigate to="summary" replace /> },
          { path: 'summary', element: <EventSummary /> },
          { path: 'players', element: <EventPlayers /> },
          {
            path: 'teams',
            element: (
              <TeamsModeGuard>
                <EventTeams />
              </TeamsModeGuard>
            ),
          },
          { path: 'flights', element: <EventFlights /> },
          { path: 'poker', element: <EventPoker /> },
          { path: 'settings', element: <EventSettings /> },
        ],
      },
      // Leaf detail page — deliberately NOT under EventDetailShell so it
      // doesn't inherit the event lifecycle header. Keeps the /events/:id/…
      // URL prefix so the event bottom-nav still resolves. It fetches its
      // own event and owns its breadcrumb/back.
      {
        path: 'events/:id/players/:playerId/scorecard',
        element: <EventPlayerScorecard />,
      },
      { path: 'users', element: <Users /> },
      { path: 'account-settings', element: <AccountSettings /> },
      { path: 'account-settings/:section', element: <AccountSettings /> },
      {
        path: 'platform',
        element: (
          <PlatformGuard>
            <Outlet />
          </PlatformGuard>
        ),
        children: [
          { index: true, element: <Navigate to="accounts" replace /> },
          { path: 'accounts', element: <AccountsList /> },
          { path: 'accounts/:id', element: <AccountDetail /> },
          { path: 'courses', element: <CoursesList /> },
          { path: 'courses/:id', element: <CourseDetail /> },
          { path: 'vipps-payments', element: <VippsPayments /> },
          { path: 'sms', element: <Sms /> },
          { path: 'reports', element: <Reports /> },
          { path: 'dev-tools', element: <DevTools /> },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
])

/**
 * Outer boundary for the lazy trees that render OUTSIDE AdminShell —
 * account-select, /registration and /o. Routes inside the shell have their own
 * boundary around its `<Outlet />`, so the sidebar and bottom nav stay mounted
 * while a page chunk loads instead of the whole app blanking.
 */
const Routes = () => (
  <Suspense fallback={null}>
    <RouterProvider router={router} />
  </Suspense>
)

export default Routes
